import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import PSHomeSectionError from '@/components/PSHomeSectionError';
import type { ApiErrorKind } from '@/src/api/apiError';

/**
 * ── /components/ps-home-section-error ────────────────────────────────────
 *
 * What a home/memberships section draws in place of its carousel when the
 * ONE `/home` (or membership-page) fetch it depends on fails. Real call
 * sites: `app/index.tsx` — Editorial Picks (light) and the All Experiences
 * grid footer (light), plus the Access Pass Exclusive block shown twice
 * (isBackgroundDark, `$brandDark`); `app/memberships/index.tsx` — all three
 * of its sections (upcoming-for-members, the four showcase carousels,
 * All plans) render it `isBackgroundDark` too, since that whole page sits on
 * `$brandDark`. So dark is the MORE common surface across the app, not the
 * exception — both are covered below.
 *
 * ── The three sentences, not two ──────────────────────────────────────────
 * The component maps `kind` to copy as: 'network' → NETWORK_MESSAGE,
 * 'server' → SERVER_MESSAGE, and everything else (which in practice is only
 * 'api' and 'unknown' — see `ApiErrorKind`) falls through the `if`s to
 * UNKNOWN_MESSAGE. SERVER_MESSAGE ("...on our end...") and UNKNOWN_MESSAGE
 * ("Something went wrong...") are DIFFERENT strings, so this is three
 * distinct sentences, not the "one shared sentence for server/api/unknown"
 * the previous holding-pen page's comment claimed — 'api' and 'unknown' do
 * share one sentence, but 'server' has its own. All three are previewed
 * below; 'api' is skipped as a fixture since it renders byte-identical to
 * 'unknown'.
 *
 * ── The art is NOT network-only, despite the component's own docstring ────
 * `PSHomeSectionError`'s docstring says the no-internet cat is "network, and
 * only network" — a leftover from a `withArt` prop the component no longer
 * takes (there's an orphaned JSDoc comment in its props type with nothing
 * attached to it, right where that prop used to be). The current code
 * renders the `<Image>` UNCONDITIONALLY, for every kind. This preview shows
 * that real, current behaviour rather than the stale doc comment — every
 * block below carries the cat, server/unknown included.
 * ─────────────────────────────────────────────────────────────────────────
 */

// The three fixture kinds, deliberately NOT four: 'api' is omitted because it
// renders identically to 'unknown' (both fall through to UNKNOWN_MESSAGE —
// see the docstring above), so a fourth block would just duplicate the third.
const PREVIEW_KINDS: { id: string; kind: ApiErrorKind; caption: string }[] = [
  {
    id: 'network',
    kind: 'network',
    caption:
      "kind='network' — NETWORK_MESSAGE, the only sentence that blames the connection instead of the app (the request never reached a server)",
  },
  {
    id: 'server',
    kind: 'server',
    caption:
      "kind='server' — SERVER_MESSAGE, a 5xx; this is the ONLY kind handleApiError() also reports to Sentry (fingerprinted per route+status), since it's the one that means something actually broke",
  },
  {
    id: 'unknown',
    kind: 'unknown',
    caption:
      "kind='unknown' (and 'api', which renders identically) — UNKNOWN_MESSAGE, the catch-all for a response we can't read or don't have server-authored text for",
  },
];

export default function PSHomeSectionErrorPreview() {
  // Drives the live block below — real screens don't animate this component
  // on retry (they swap it for a skeleton/carousel once the underlying fetch
  // hook resolves), so there's nothing to visually replay in isolation. What
  // IS worth proving live is that "Retry" is a genuinely wired PSLinkButton,
  // not a picture of one — tapping it fires `onRetry` and this counter moves.
  const [retries, setRetries] = useState(0);

  return (
    <>
      <Head>
        <title>PSHomeSectionError · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* live — proves the nested "Retry" PSLinkButton (see its own preview
            page for why it's a Text, not a Button) is actually tappable here,
            not just laid out. Mirrors Editorial Picks' real wiring: `onRetry`
            is the hook's `retry`/`refetch`, called with no args. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — tap Retry; fires onRetry for real (retried {retries} time
              {retries === 1 ? '' : 's'})
            </Text>
            <PSHomeSectionError
              kind="network"
              onRetry={() => setRetries((n) => n + 1)}
            />
          </YStack>

          {/* Light surface — Editorial Picks and the All Experiences grid
            footer in app/index.tsx, the two sections NOT inside the
            `$brandDark` Access Pass block. `minHeight={CARD_HEIGHT}` (see the
            component) is what keeps the page's shape here — it's a carousel
            card's worth of height even with no carousel in it. */}
          <YStack alignSelf="stretch" gap="$lg">
            {PREVIEW_KINDS.map((k) => (
              <YStack key={k.id} gap="$xs">
                <Text
                  fontFamily="$body"
                  fontSize="$small"
                  color="$textDarkMuted"
                >
                  {k.caption}
                </Text>
                <PSHomeSectionError kind={k.kind} onRetry={() => {}} />
              </YStack>
            ))}
          </YStack>

          {/* Dark surface — `isBackgroundDark`, shown against the actual
            `$brandDark` block it's designed for (RN text doesn't cascade, so
            rendering these on the page's near-white default would read as a
            colour bug, not the real thing). This is the MORE common call
            site: home's Access Pass Exclusive block (×2) and every one of
            memberships/index.tsx's three sections use this variant. The cat
            tint flips too (see PSHomeSectionError: `color.borderColor` on
            dark vs `color.brand` on light) so it stays visible against the
            dark fill instead of vanishing as a near-black shape on
            near-black. */}
          <YStack bg="$color.brandDark" p="$space.xl" gap="$lg" rounded="$base">
            {PREVIEW_KINDS.map((k) => (
              <YStack key={`${k.id}-dark`} gap="$xs">
                <Text
                  fontFamily="$body"
                  fontSize="$small"
                  color="$textWhiteMuted"
                >
                  {k.caption}, onDark
                </Text>
                <PSHomeSectionError
                  kind={k.kind}
                  onRetry={() => {}}
                  isBackgroundDark
                />
              </YStack>
            ))}
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
